import { useCallback, useId, useState } from 'react'

import type { CommandDeckContinueResolution } from '../lib/resolveCommandDeckContinue'
import type { CommandDeckPreviewBundle } from '../contracts/types'
import { CommandDeckStatusPill } from './CommandDeckStatusPill'
import { spatialMapStatusToPillVariant } from './commandDeckStatusPillPresentational'
import { deriveSpatialMapModel, type SpatialMapNode } from './spatialMapModel'

interface Props {
  bundle: CommandDeckPreviewBundle
  continueResolution: CommandDeckContinueResolution
}

function nodeClassName(node: SpatialMapNode): string {
  const parts = ['cd-mc-living-map__node', `cd-mc-living-map__node--${node.status}`]
  if (node.isFocus) parts.push('cd-mc-living-map__node--focus')
  if (node.isActiveSurface) parts.push('cd-mc-living-map__node--surface')
  if (node.isRouteTarget) parts.push('cd-mc-living-map__node--route')
  return parts.join(' ')
}

function nodeAriaLabel(node: SpatialMapNode): string {
  const parts = [node.label, node.statusLabel]
  if (node.isRouteTarget) parts.push('Continue route layer')
  if (node.isFocus) parts.push('current focus')
  return parts.join(', ')
}

export function CommandDeckSpatialMap({ bundle, continueResolution }: Props) {
  const detailId = useId()
  const model = deriveSpatialMapModel(bundle, continueResolution)
  const [inspectedId, setInspectedId] = useState<string | null>(null)
  const [hoveredId, setHoveredId] = useState<string | null>(null)

  const inspected = model.nodes.find((n) => n.id === (inspectedId ?? hoveredId)) ?? null

  const handleInspect = useCallback((nodeId: string) => {
    setInspectedId((prev) => (prev === nodeId ? null : nodeId))
  }, [])

  return (
    <div className="cd-mc-living-map-stack">
      <div
        className="cd-mc-living-map"
        role="group"
        aria-label="ORBIS spatial command map"
        data-route-target={model.nodes.some((n) => n.isRouteTarget) ? 'true' : undefined}
      >
      <div className="cd-mc-living-map__depth" aria-hidden="true">
        <div className="cd-mc-living-map__ring cd-mc-living-map__ring--outer" />
        <div className="cd-mc-living-map__ring cd-mc-living-map__ring--inner" />
        <div className="cd-mc-living-map__sweep" />
      </div>

      <svg className="cd-mc-living-map__links" viewBox="-100 -100 200 200" aria-hidden="true">
        {model.nodes.map((node) => {
          const rad = (node.angleDeg * Math.PI) / 180
          const x = Math.cos(rad) * 72
          const y = Math.sin(rad) * 72
          const active = node.isFocus || node.isRouteTarget
          return (
            <line
              key={node.id}
              x1={0}
              y1={0}
              x2={x}
              y2={y}
              className={
                active
                  ? 'cd-mc-living-map__link cd-mc-living-map__link--active'
                  : 'cd-mc-living-map__link'
              }
              data-node={node.id}
            />
          )
        })}
      </svg>

      <div className="cd-mc-living-map__core" aria-hidden="false">
        <span className="cd-mc-living-map__core-name">{model.projectName}</span>
        <span className="cd-mc-living-map__core-tag">Mission core</span>
      </div>

      {model.nodes.map((node) => (
        <button
          key={node.id}
          type="button"
          className={nodeClassName(node)}
          style={{
            transform: `rotate(${node.angleDeg}deg) translate(var(--cd-map-orbit-r, 5.5rem)) rotate(${-node.angleDeg}deg)`,
          }}
          aria-label={nodeAriaLabel(node)}
          aria-current={node.isFocus ? 'true' : undefined}
          aria-describedby={inspected?.id === node.id ? detailId : undefined}
          aria-pressed={inspectedId === node.id}
          data-status={node.status}
          data-node={node.id}
          onClick={() => handleInspect(node.id)}
          onMouseEnter={() => setHoveredId(node.id)}
          onMouseLeave={() => setHoveredId(null)}
          onFocus={() => setHoveredId(node.id)}
          onBlur={() => setHoveredId(null)}
        >
          <CommandDeckStatusPill
            label={node.statusLabel.toUpperCase()}
            variant={spatialMapStatusToPillVariant(node.status)}
            className="cd-mc-living-map__status-pill"
          />
          <span className="cd-mc-living-map__label">{node.label}</span>
        </button>
      ))}
      </div>

      <div
        className={`cd-mc-living-map__overlay${inspected ? ' cd-mc-living-map__overlay--active' : ''}`}
        aria-live="polite"
      >
        <div className="cd-mc-living-map__hud">
          <p className="cd-mc-living-map__hud-focus">
            <span className="cd-mc-living-map__hud-k">Focus</span>
            {model.focusSummary}
          </p>
          {model.routeLabel ? (
            <p className="cd-mc-living-map__hud-route">
              <span className="cd-mc-living-map__hud-k">Route</span>
              {model.routeLabel}
            </p>
          ) : null}
        </div>

        <div
          id={detailId}
          className="cd-mc-living-map__inspect"
          role="status"
          data-hovered={inspected ? 'true' : 'false'}
        >
          {inspected ? (
            <>
              <p className="cd-mc-living-map__inspect-head">
                <span className="cd-mc-living-map__inspect-label">{inspected.label}</span>
                <span className="cd-mc-living-map__inspect-status">{inspected.statusLabel}</span>
              </p>
              <p className="cd-mc-living-map__detail cd-mc-living-map__detail--visible">
                {inspected.inspectDetail}
              </p>
            </>
          ) : (
            <p className="cd-mc-living-map__detail">
              Hover or focus a layer to inspect status. Selection is UI-only and does not run
              workflows.
            </p>
          )}
        </div>
      </div>
    </div>
  )
}
